import { useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { reservationQueryKeys } from '~/hooks/apis/reservations/reservationQueryKeys';
import { useApplyBlackoutDates } from '~/hooks/apis/stopsell/useApplyBlackoutDates';

import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import { SourceBlackoutRange } from './blackoutReconciliation';
import { buildBlackoutApplyPayload, buildRoomTypeApplyPlans, buildRoomTypeMergePlans } from './buildBlackoutApplyPlan';
import type { SalesforceBlackoutDate } from './types';

// Caller supplies only its own source's ranges; the other stays empty, so no SF-vs-doc conflict step is needed.
export function useBlackoutApplyPlan({
  blackoutDates,
  docRangesByRoomType,
  blackoutDatesByRoomRate,
  propertyId,
  roomRateSelections,
  roomTypeNames,
  onApplied,
}: {
  blackoutDates: Array<SalesforceBlackoutDate>;
  docRangesByRoomType: Record<string, Array<SourceBlackoutRange>>;
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>;
  propertyId: string;
  roomRateSelections: Array<RoomRateSelection>;
  roomTypeNames: Array<string>;
  onApplied: () => void;
}) {
  const queryClient = useQueryClient();
  const applyBlackoutDates = useApplyBlackoutDates(propertyId);
  const [excludedRoomTypeIds, setExcludedRoomTypeIds] = useState<Set<string>>(new Set());

  const mergePlans = useMemo(
    () => buildRoomTypeMergePlans(blackoutDates, docRangesByRoomType, roomRateSelections, roomTypeNames),
    [blackoutDates, docRangesByRoomType, roomRateSelections, roomTypeNames],
  );

  const applyPlans = useMemo(
    () => buildRoomTypeApplyPlans(mergePlans, blackoutDatesByRoomRate),
    [mergePlans, blackoutDatesByRoomRate],
  );

  const changedPlans = applyPlans.filter((plan) => plan.blocksToDelete.length > 0 || plan.rangesToCreate.length > 0);

  const includedPlans = changedPlans.filter((plan) => !excludedRoomTypeIds.has(plan.roomTypeId));
  const totalToCreate = includedPlans.reduce((sum, plan) => sum + plan.rangesToCreate.length, 0);
  const totalToDelete = includedPlans.reduce((sum, plan) => sum + plan.blocksToDelete.length, 0);

  const toggleRoomType = (roomTypeId: string, checked: boolean) => {
    setExcludedRoomTypeIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.delete(roomTypeId);
      } else {
        next.add(roomTypeId);
      }
      return next;
    });
  };

  const handleApply = () => {
    const includedRoomTypeIds = new Set(includedPlans.map((plan) => plan.roomTypeId));
    const payload = buildBlackoutApplyPayload(changedPlans, includedRoomTypeIds);
    applyBlackoutDates.mutate(payload, {
      onSuccess: () => {
        // useApplyBlackoutDates invalidates ['blackoutDates', propertyId], which nothing here reads.
        queryClient.invalidateQueries({ queryKey: reservationQueryKeys.blackoutDatesByRoomRateAll(propertyId) });
        onApplied();
      },
    });
  };

  return {
    changedPlans,
    excludedRoomTypeIds,
    toggleRoomType,
    totalToCreate,
    totalToDelete,
    handleApply,
    isApplying: applyBlackoutDates.isPending,
  };
}
